import type {
  Comment,
  CommentResult,
  EventSnapshot,
  FlowerReceipt,
  LikeResult,
  MyEngagement,
} from '../types';
import { demoComments, demoEngagement, demoSnapshot } from './demoData';
import { isSupabaseConfigured, supabase } from './supabase';

export interface PantherApi {
  ensureSession(): Promise<string>;
  getSnapshot(): Promise<EventSnapshot>;
  getComments(topicId: string): Promise<Comment[]>;
  getMyEngagement(topicId: string): Promise<MyEngagement>;
  getMyEngagements(topicIds: string[]): Promise<MyEngagement[]>;
  setLike(topicId: string, targetState: boolean, generation?: number): Promise<LikeResult>;
  applyFlowers(
    topicId: string,
    batchId: string,
    delta: number,
    generation?: number,
  ): Promise<FlowerReceipt>;
  createComment(
    topicId: string,
    nickname: string,
    body: string,
    generation?: number,
  ): Promise<CommentResult>;
}

function requireData<T>(data: T | null, message: string): T {
  if (data === null) throw new Error(message);
  return data;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requiredString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  if (typeof value !== 'string') throw new Error(`Invalid event data: ${key}.`);
  return value;
}

function requiredNumber(record: Record<string, unknown>, key: string): number {
  const value = record[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Invalid event data: ${key}.`);
  }
  return value;
}

export function parseEventSnapshot(value: unknown): EventSnapshot {
  if (!isRecord(value) || !isRecord(value.settings) || !Array.isArray(value.topics)) {
    throw new Error('Invalid event snapshot.');
  }
  const settings = value.settings;
  const currentTopicId = settings.currentTopicId;
  const changedAt = settings.currentTopicChangedAt;
  if (
    (currentTopicId !== null && typeof currentTopicId !== 'string') ||
    (changedAt !== null && typeof changedAt !== 'string') ||
    typeof settings.commentsEnabled !== 'boolean' ||
    typeof settings.eventEnabled !== 'boolean' ||
    typeof settings.audienceFollowEnabled !== 'boolean' ||
    (settings.interactionGeneration !== undefined &&
      (typeof settings.interactionGeneration !== 'number' ||
        !Number.isSafeInteger(settings.interactionGeneration) ||
        settings.interactionGeneration < 0))
  ) {
    throw new Error('Invalid event settings.');
  }

  return {
    settings: {
      eventTitle: requiredString(settings, 'eventTitle'),
      currentTopicId,
      currentTopicChangedAt: changedAt,
      audienceFollowEnabled: settings.audienceFollowEnabled,
      interactionGeneration:
        typeof settings.interactionGeneration === 'number'
          ? settings.interactionGeneration
          : 0,
      audienceBrowsingEnabled:
        typeof settings.audienceBrowsingEnabled === 'boolean'
          ? settings.audienceBrowsingEnabled
          : false,
      commentsEnabled: settings.commentsEnabled,
      eventEnabled: settings.eventEnabled,
    },
    topics: value.topics.map((item) => {
      if (!isRecord(item)) throw new Error('Invalid topic data.');
      const displayOrder =
        typeof item.displayOrder === 'number'
          ? item.displayOrder
          : requiredNumber(item, 'sortOrder');
      if (
        (item.status !== 'open' && item.status !== 'closed') ||
        typeof item.isPublished !== 'boolean'
      ) {
        throw new Error('Invalid topic state.');
      }
      return {
        id: requiredString(item, 'id'),
        title: requiredString(item, 'title'),
        presenter: requiredString(item, 'presenter'),
        description: requiredString(item, 'description'),
        displayOrder,
        status: item.status,
        isPublished: item.isPublished,
        discussionQuestion:
          item.discussionQuestion === null || item.discussionQuestion === undefined
            ? null
            : requiredString(item, 'discussionQuestion'),
        discussionOptions: Array.isArray(item.discussionOptions)
          ? item.discussionOptions.map((option) => {
              if (typeof option !== 'string') {
                throw new Error('Invalid discussion option.');
              }
              return option;
            })
          : [],
        likeCount: requiredNumber(item, 'likeCount'),
        flowerCount: requiredNumber(item, 'flowerCount'),
        commentCount: requiredNumber(item, 'commentCount'),
      };
    }),
  };
}

class SupabasePantherApi implements PantherApi {
  async ensureSession(): Promise<string> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const existing = await supabase.auth.getSession();
    if (existing.error) throw existing.error;
    if (existing.data.session?.user.id) return existing.data.session.user.id;

    const created = await supabase.auth.signInAnonymously();
    if (created.error) throw created.error;
    return requireData(created.data.user?.id ?? null, 'Anonymous sign-in failed.');
  }

  async getSnapshot(): Promise<EventSnapshot> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.rpc('get_event_snapshot');
    if (error) throw error;
    return parseEventSnapshot(requireData(data, 'Event data is unavailable.'));
  }

  async getComments(topicId: string): Promise<Comment[]> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.rpc('get_topic_comments', {
      p_topic_id: topicId,
    });
    if (error) throw error;
    return ((data ?? []) as Array<{
      id: string;
      topic_id: string;
      nickname: string;
      body: string;
      created_at: string;
    }>).map((comment) => ({
      id: comment.id,
      topicId: comment.topic_id,
      nickname: comment.nickname,
      body: comment.body,
      createdAt: comment.created_at,
    }));
  }

  async getMyEngagement(topicId: string): Promise<MyEngagement> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.rpc('get_my_engagement', {
      p_topic_id: topicId,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return { topicId, liked: false, flowerCount: 0 };
    const typed = row as {
      topic_id: string;
      liked: boolean;
      flower_count: number;
    };
    return {
      topicId: typed.topic_id,
      liked: typed.liked,
      flowerCount: typed.flower_count,
    };
  }

  async getMyEngagements(topicIds: string[]): Promise<MyEngagement[]> {
    if (!supabase) throw new Error('Supabase is not configured.');
    if (!topicIds.length) return [];
    const { data, error } = await supabase
      .from('engagements')
      .select('topic_id, liked, flower_total')
      .in('topic_id', topicIds);
    if (error) throw error;

    const byTopic = new Map(
      ((data ?? []) as Array<{
        topic_id: string;
        liked: boolean;
        flower_total: number;
      }>).map((row) => [row.topic_id, row]),
    );
    return topicIds.map((topicId) => {
      const row = byTopic.get(topicId);
      return {
        topicId,
        liked: row?.liked ?? false,
        flowerCount: row?.flower_total ?? 0,
      };
    });
  }

  async setLike(topicId: string, targetState: boolean, generation = 0): Promise<LikeResult> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.rpc('toggle_topic_like', {
      p_topic_id: topicId,
      p_target_state: targetState,
      p_generation: generation,
    });
    if (error) throw error;
    return requireData(data as LikeResult | null, 'Like update returned no data.');
  }

  async applyFlowers(
    topicId: string,
    batchId: string,
    delta: number,
    generation = 0,
  ): Promise<FlowerReceipt> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.rpc('apply_flower_batch', {
      p_topic_id: topicId,
      p_batch_id: batchId,
      p_delta: delta,
      p_generation: generation,
    });
    if (error) throw error;
    return requireData(
      data as FlowerReceipt | null,
      'Flower update returned no data.',
    );
  }

  async createComment(
    topicId: string,
    nickname: string,
    body: string,
    generation = 0,
  ): Promise<CommentResult> {
    if (!supabase) throw new Error('Supabase is not configured.');
    const { data, error } = await supabase.rpc('create_public_comment', {
      p_topic_id: topicId,
      p_nickname: nickname,
      p_body: body,
      p_generation: generation,
    });
    if (error) throw error;
    return requireData(
      data as CommentResult | null,
      'Comment update returned no data.',
    );
  }
}

export class DemoPantherApi implements PantherApi {
  private snapshot = structuredClone(demoSnapshot);
  private comments = structuredClone(demoComments);
  private engagement = new Map<string, MyEngagement>([
    [demoEngagement.topicId, structuredClone(demoEngagement)],
  ]);
  private receipts = new Set<string>();

  async ensureSession(): Promise<string> {
    return 'demo-user';
  }

  async getSnapshot(): Promise<EventSnapshot> {
    return structuredClone(this.snapshot);
  }

  async getComments(topicId: string): Promise<Comment[]> {
    return structuredClone(
      this.comments.filter((comment) => comment.topicId === topicId),
    );
  }

  async getMyEngagement(topicId: string): Promise<MyEngagement> {
    return structuredClone(
      this.engagement.get(topicId) ?? { topicId, liked: false, flowerCount: 0 },
    );
  }

  async getMyEngagements(topicIds: string[]): Promise<MyEngagement[]> {
    return Promise.all(topicIds.map((topicId) => this.getMyEngagement(topicId)));
  }

  async setLike(topicId: string, targetState: boolean): Promise<LikeResult> {
    const engagement =
      this.engagement.get(topicId) ?? { topicId, liked: false, flowerCount: 0 };
    const topic = this.snapshot.topics.find((item) => item.id === topicId);
    if (!topic) throw new Error('Topic not found.');
    if (engagement.liked !== targetState) {
      topic.likeCount += targetState ? 1 : -1;
    }
    engagement.liked = targetState;
    this.engagement.set(topicId, engagement);
    return { topicId, liked: targetState, likeCount: topic.likeCount };
  }

  async applyFlowers(
    topicId: string,
    batchId: string,
    delta: number,
  ): Promise<FlowerReceipt> {
    const engagement =
      this.engagement.get(topicId) ?? { topicId, liked: false, flowerCount: 0 };
    const topic = this.snapshot.topics.find((item) => item.id === topicId);
    if (!topic) throw new Error('Topic not found.');
    const applied = !this.receipts.has(batchId);
    if (applied) {
      this.receipts.add(batchId);
      engagement.flowerCount += delta;
      topic.flowerCount += delta;
      this.engagement.set(topicId, engagement);
    }
    return { topicId, flowerCount: topic.flowerCount, applied };
  }

  async createComment(
    topicId: string,
    nickname: string,
    body: string,
  ): Promise<CommentResult> {
    const topic = this.snapshot.topics.find((item) => item.id === topicId);
    if (!topic) throw new Error('Topic not found.');
    const comment: Comment = {
      id: crypto.randomUUID(),
      topicId,
      nickname,
      body,
      createdAt: new Date().toISOString(),
    };
    this.comments = [comment, ...this.comments];
    topic.commentCount += 1;
    return { comment, commentCount: topic.commentCount };
  }
}

const useDemo = import.meta.env.VITE_USE_DEMO_DATA === 'true';
export const api: PantherApi = useDemo
  ? new DemoPantherApi()
  : new SupabasePantherApi();

export const configurationReady = useDemo || isSupabaseConfigured;
export const demoMode = useDemo;
